import {
  Button,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Tag,
} from "antd";
import React, { useEffect } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  LoanConfig,
  LoanConfigStatus,
  useUpsertLoanConfig,
} from "../../api/loanConfig";
import { Bank } from "../../api/banks";

const loanConfigSchema = z
  .object({
    bankId: z.preprocess(
      (val) =>
        val === undefined || val === null || val === "" ? undefined : Number(val),
      z.number({ message: "Bank is required" }).int().positive(),
    ),
    loanProductId: z
      .string()
      .min(1, { message: "Loan product id is required" })
      .max(255),
    qenaApiBaseUrl: z
      .string()
      .max(512, { message: "Base URL is too long" })
      .optional()
      .or(z.literal("")),
    qenaApiKey: z
      .string()
      .max(512, { message: "API key is too long" })
      .optional()
      .or(z.literal("")),
    qenaCallbackUrl: z
      .string()
      .max(512, { message: "Callback URL is too long" })
      .optional()
      .or(z.literal("")),
    status: z.enum(["ACTIVE", "INACTIVE"]),

    loanInterest: z.number().positive({ message: "Must be > 0" }),
    minLoanAmount: z.number().positive({ message: "Must be > 0" }),
    loanCap: z.number().positive({ message: "Must be > 0" }),
    maxLoanAmount: z.number().positive({ message: "Must be > 0" }),
    minInterest: z.number().positive({ message: "Must be > 0" }),
    maxInterest: z.number().positive({ message: "Must be > 0" }),
    minRepaymentDays: z
      .number()
      .int({ message: "Must be a whole number" })
      .min(1, { message: "Must be ≥ 1" }),
    maxRepaymentDays: z
      .number()
      .int({ message: "Must be a whole number" })
      .min(1, { message: "Must be ≥ 1" }),
  })
  .refine((v) => v.maxLoanAmount >= v.minLoanAmount, {
    message: "Max loan must be ≥ min loan",
    path: ["maxLoanAmount"],
  })
  .refine((v) => v.maxInterest >= v.minInterest, {
    message: "Max interest must be ≥ min interest",
    path: ["maxInterest"],
  })
  .refine((v) => v.maxRepaymentDays >= v.minRepaymentDays, {
    message: "Max repayment days must be ≥ min",
    path: ["maxRepaymentDays"],
  });

type LoanConfigForm = z.infer<typeof loanConfigSchema>;

type LoanConfigModalProps = {
  open: boolean;
  /** Existing config to edit; pass `undefined` for create. */
  selectedConfig?: LoanConfig | null;
  /** Banks selectable in the bank picker. Pre-fetched by the parent page. */
  banks: Bank[];
  /**
   * If provided, the bank picker is locked to this bank id (e.g. when the
   * parent page has already filtered by bank). Pass `undefined` to allow
   * choosing any active bank.
   */
  lockedBankId?: number;
  hideModal: () => void;
};

const blankForm = (defaults?: Partial<LoanConfigForm>): LoanConfigForm => ({
  bankId: defaults?.bankId ?? (undefined as unknown as number),
  loanProductId: defaults?.loanProductId ?? "",
  qenaApiBaseUrl: defaults?.qenaApiBaseUrl ?? "",
  qenaApiKey: "",
  qenaCallbackUrl: defaults?.qenaCallbackUrl ?? "",
  status: defaults?.status ?? "ACTIVE",

  loanInterest: defaults?.loanInterest ?? (undefined as unknown as number),
  minLoanAmount: defaults?.minLoanAmount ?? (undefined as unknown as number),
  loanCap: defaults?.loanCap ?? (undefined as unknown as number),
  maxLoanAmount: defaults?.maxLoanAmount ?? (undefined as unknown as number),
  minInterest: defaults?.minInterest ?? (undefined as unknown as number),
  maxInterest: defaults?.maxInterest ?? (undefined as unknown as number),
  minRepaymentDays:
    defaults?.minRepaymentDays ?? (undefined as unknown as number),
  maxRepaymentDays:
    defaults?.maxRepaymentDays ?? (undefined as unknown as number),
});

const LoanConfigModal: React.FC<LoanConfigModalProps> = ({
  open,
  selectedConfig,
  banks,
  lockedBankId,
  hideModal,
}) => {
  const { mutateAsync: upsert, isPending } = useUpsertLoanConfig();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<LoanConfigForm>({
    resolver: zodResolver(loanConfigSchema) as any,
    defaultValues: blankForm({ bankId: lockedBankId }),
    mode: "onChange",
  });

  useEffect(() => {
    if (!open) return;
    if (selectedConfig) {
      reset(
        blankForm({
          bankId: selectedConfig.bankId ?? lockedBankId,
          loanProductId: selectedConfig.loanProductId,
          qenaApiBaseUrl: selectedConfig.qenaApiBaseUrl ?? "",
          qenaCallbackUrl: selectedConfig.qenaCallbackUrl ?? "",
          status: selectedConfig.status,
          loanInterest: selectedConfig.loanInterest,
          minLoanAmount: selectedConfig.minLoanAmount,
          loanCap: selectedConfig.loanCap,
          maxLoanAmount: selectedConfig.maxLoanAmount,
          minInterest: selectedConfig.minInterest,
          maxInterest: selectedConfig.maxInterest,
          minRepaymentDays: selectedConfig.minRepaymentDays,
          maxRepaymentDays: selectedConfig.maxRepaymentDays,
        }),
      );
    } else {
      reset(blankForm({ bankId: lockedBankId }));
    }
  }, [open, selectedConfig, lockedBankId, reset]);

  const onSubmit: SubmitHandler<LoanConfigForm> = async (form) => {
    try {
      await upsert({
        bankId: form.bankId,
        loanProductId: form.loanProductId.trim(),
        qenaApiBaseUrl: form.qenaApiBaseUrl?.trim() || null,
        qenaApiKey: form.qenaApiKey?.trim() || undefined,
        qenaCallbackUrl: form.qenaCallbackUrl?.trim() || null,
        status: form.status as LoanConfigStatus,
        loanInterest: form.loanInterest,
        minLoanAmount: form.minLoanAmount,
        loanCap: form.loanCap,
        maxLoanAmount: form.maxLoanAmount,
        minInterest: form.minInterest,
        maxInterest: form.maxInterest,
        minRepaymentDays: form.minRepaymentDays,
        maxRepaymentDays: form.maxRepaymentDays,
      });
      message.success(
        selectedConfig
          ? "Loan config updated successfully"
          : "Loan config created successfully",
      );
      hideModal();
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Operation failed";
      message.error(
        typeof apiMessage === "string" ? apiMessage : "Operation failed",
      );
    }
  };

  const bankOptions = banks
    .filter((b) => b.isActive || b.id === selectedConfig?.bankId)
    .map((b) => ({
      label: `${b.bankName}${b.code ? ` (${b.code})` : ""}`,
      value: b.id,
    }));

  return (
    <Modal
      destroyOnClose
      open={open}
      centered
      width={720}
      onCancel={hideModal}
      title={
        <div className="flex flex-col items-center justify-center font-semibold text-black text-[22px]">
          {selectedConfig ? "Edit Loan Config" : "Create Loan Config"}
        </div>
      }
      footer={
        <div className="flex justify-end gap-3 p-4">
          <Button
            onClick={hideModal}
            className="!border-gray-300 !text-gray-700 font-medium hover:!bg-gray-100 hover:!text-gray-900 transition-all duration-200 bg-transparent"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            className="!bg-[#009602] !border-none hover:!bg-[#00b306] font-medium transition-all duration-200"
            loading={isPending}
            disabled={isPending}
          >
            {selectedConfig ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank *
            </label>
            <Controller
              name="bankId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Select bank"
                  value={field.value || undefined}
                  onChange={(val) => field.onChange(val)}
                  style={{ width: "100%" }}
                  options={bankOptions}
                  disabled={!!lockedBankId || !!selectedConfig}
                  status={errors.bankId ? "error" : undefined}
                  showSearch
                  optionFilterProp="label"
                />
              )}
            />
            {errors.bankId && (
              <span className="text-red-600 text-xs">
                {errors.bankId.message as string}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan product id *
            </label>
            <Controller
              name="loanProductId"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. CBE-INPUT-LOAN-001"
                  status={errors.loanProductId ? "error" : undefined}
                  disabled={!!selectedConfig}
                />
              )}
            />
            {errors.loanProductId && (
              <span className="text-red-600 text-xs">
                {errors.loanProductId.message}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  style={{ width: "100%" }}
                  options={[
                    { label: "Active", value: "ACTIVE" },
                    { label: "Inactive", value: "INACTIVE" },
                  ]}
                />
              )}
            />
          </div>
          <div className="self-end">
            {selectedConfig?.qenaApiKeyConfigured && (
              <Tag color="green" className="border-0 bg-green-100 text-green-700">
                Qena API key configured
              </Tag>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qena API base URL
          </label>
          <Controller
            name="qenaApiBaseUrl"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="https://qena.bank.example/api"
                status={errors.qenaApiBaseUrl ? "error" : undefined}
              />
            )}
          />
          {errors.qenaApiBaseUrl && (
            <span className="text-red-600 text-xs">
              {errors.qenaApiBaseUrl.message}
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qena API key{" "}
            {selectedConfig?.qenaApiKeyConfigured && (
              <span className="text-gray-400 font-normal">
                (leave blank to keep existing)
              </span>
            )}
          </label>
          <Controller
            name="qenaApiKey"
            control={control}
            render={({ field }) => (
              <Input.Password
                {...field}
                placeholder={
                  selectedConfig?.qenaApiKeyConfigured
                    ? "•••••• (unchanged)"
                    : "Enter Qena API key"
                }
                autoComplete="new-password"
                status={errors.qenaApiKey ? "error" : undefined}
              />
            )}
          />
          {errors.qenaApiKey && (
            <span className="text-red-600 text-xs">
              {errors.qenaApiKey.message}
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qena callback URL
          </label>
          <Controller
            name="qenaCallbackUrl"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="https://shegafre.example/dagri/api/qena/callback/..."
                status={errors.qenaCallbackUrl ? "error" : undefined}
              />
            )}
          />
          {errors.qenaCallbackUrl && (
            <span className="text-red-600 text-xs">
              {errors.qenaCallbackUrl.message}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {(
            [
              { name: "loanInterest", label: "Loan interest (%)" },
              { name: "minInterest", label: "Min interest (%)" },
              { name: "maxInterest", label: "Max interest (%)" },
              { name: "loanCap", label: "Loan cap (ETB)" },
              { name: "minLoanAmount", label: "Min loan amount (ETB)" },
              { name: "maxLoanAmount", label: "Max loan amount (ETB)" },
            ] as const
          ).map(({ name, label }) => (
            <div key={name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} *
              </label>
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <InputNumber
                    className="w-full"
                    placeholder={label}
                    value={field.value as number | undefined}
                    onChange={(val) =>
                      field.onChange(val === null ? undefined : val)
                    }
                    status={errors[name] ? "error" : undefined}
                    min={0}
                    step={0.01}
                  />
                )}
              />
              {errors[name] && (
                <span className="text-red-600 text-xs">
                  {(errors as any)[name]?.message}
                </span>
              )}
            </div>
          ))}

          {(
            [
              { name: "minRepaymentDays", label: "Min repayment days" },
              { name: "maxRepaymentDays", label: "Max repayment days" },
            ] as const
          ).map(({ name, label }) => (
            <div key={name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} *
              </label>
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <InputNumber
                    className="w-full"
                    placeholder={label}
                    value={field.value as number | undefined}
                    onChange={(val) =>
                      field.onChange(val === null ? undefined : val)
                    }
                    status={errors[name] ? "error" : undefined}
                    min={1}
                    step={1}
                  />
                )}
              />
              {errors[name] && (
                <span className="text-red-600 text-xs">
                  {(errors as any)[name]?.message}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default LoanConfigModal;
